import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {ShopList} from "./ShopList";
import {boxShadow, getTheme} from "./themes";
import {AppMode, getOccupancyOrder, Marketplace, Order, OrderState, Shop, Shops, ShopType} from "./types";
import {
    classNames,
    layout,
    typography,
    useActivateListener,
    useBarcodeListener,
    useCanAutoDeactivateListener,
    useLocationCode,
    useOccupancies,
    useStorageItemValue
} from "@variocube/cube-ui";
import {Barcode, LauncherItem, Occupancy} from "@variocube/app-api";
import {stylesheet} from "typestyle";
import pickupIcon from "./generic/pickup.svg";
import {CodeDialog} from "./code";
import {PickupFlow} from "./pickup";
import {StoreFlow} from "./store";
import {ShopAccessView} from "./shop";
import {defined} from "@variocube/app-common";
import {DryCleaningHomeView} from "./drycleaning";
import {GenericShopHomeView} from "./generic";
import {logger} from "./logger";
import {getShopLauncherItem, useMarketplaceLauncherItem} from "./launcher";
import {useLocalization} from "../i18n";

const log = logger.sub("Main");

interface MainProps {
    onChangeMode: (mode: AppMode) => void;
    onChangeLauncherItems: (launcherItems: LauncherItem[]) => void;
}

export function Main({onChangeMode, onChangeLauncherItems}: MainProps) {
    const {t} = useLocalization();
    const locationCode = useLocationCode();

    const [currentShopId, setCurrentShopId] = useState<string | undefined>(undefined);
    const shops = useStorageItemValue<Shops>("shops");
    const marketplace = useStorageItemValue<Marketplace>("marketplace");

    const occupancies = useOccupancies();

    const [codeOpen, setCodeOpen] = useState(false);
    const handleOpenCodeClick = useCallback(() => setCodeOpen(true), []);
    const handleCloseCodeClick = useCallback(() => setCodeOpen(false), []);

    const [orderPickup, setOrderPickup] = useState<Occupancy | undefined>(undefined);
    const [orderStore, setOrderStore] = useState<Occupancy | undefined>(undefined);
    const [shopAccess, setShopAccess] = useState<string>();
    const [orderDelivery, setOrderDelivery] = useState<Order | undefined>(undefined);

    useActivateListener(useCallback((item?: LauncherItem) => {
        log.verbose("App activated", item);
        setOrderPickup(undefined);
        setShopAccess(undefined);
        setOrderDelivery(undefined);
        setCodeOpen(false);
        if (item) {
            setCurrentShopId(item.shop);
        }
    }, []));

    useCanAutoDeactivateListener(useCallback(() => {
        return !orderPickup || defined(shopAccess);
    }, [orderPickup, shopAccess]));

    const handlePickupDone = useCallback(() => {
        setOrderPickup(undefined);
        setCodeOpen(false);
    }, []);

    const handleStoreDone = useCallback(() => {
        setOrderStore(undefined);
        setCodeOpen(false);
    }, []);

    const handleShopDone = useCallback(() => {
        setShopAccess(undefined);
        setCodeOpen(false);
        setOrderDelivery(undefined);
    }, []);

    // auto-select single shop and change mode if it is DryCleaning
    const mode = useMemo(() => {
        if (shops && !currentShopId) {
            const singleShop = getSingleShop(shops);
            setCurrentShopId(singleShop?.id);

            if (singleShop && singleShop.shopType == ShopType.DryCleaning) {
                return AppMode.DryCleaning;
            }
        }
        return AppMode.Marketplace;
    }, [shops, onChangeMode, currentShopId]);

    useEffect(() => {
        onChangeMode(mode);
    }, [mode, onChangeMode]);

    const marketplaceLauncherItem = useMarketplaceLauncherItem(marketplace, mode);

    // compute launcher items
    useEffect(() => {
        const launcherItems: LauncherItem[] = [];
        if (shops) {
            const singleShop = getSingleShop(shops);
            const entries = Object.values(shops);
            if (singleShop) {
                launcherItems.push(getShopLauncherItem(singleShop, t("appDescriptionDryCleaning")));
            }
            else {
                const dryCleanerItems = entries.filter((shop) => shop.shopType == ShopType.DryCleaning)
                    .map(shop => getShopLauncherItem(shop, t("appDescriptionDryCleaning")));
                launcherItems.push(...dryCleanerItems);
                launcherItems.push(marketplaceLauncherItem);
            }
        }
        else {
            // show marketplace launcher item while loading
            launcherItems.push(marketplaceLauncherItem);
        }
        onChangeLauncherItems(launcherItems);
    }, [shops, marketplaceLauncherItem, onChangeLauncherItems]);


    const handleSelectShop = useCallback((shop: Shop) => setCurrentShopId(shop.id), []);
    const clearCurrentShop = useCallback(() => setCurrentShopId(undefined), []);

    const handleCodeVerify = useCallback(async (code: string) => {
        log.verbose("Verifying code", code);
        if (!code) {
            throw new Error("Invalid code");
        }
        if (shops) {
            // check for shop access code
            const shop = Object.values(shops).filter(shop => shop.accessCode == code).pop();
            if (shop) {
                log.info("Shop access granted", shop);
                return setShopAccess(shop.id);
            }

            // check for occupancy access code
            const occupancyOrder = findOccupancyOrder(occupancies, shops, code);
            if (occupancyOrder) {
                const {occupancy, order} = occupancyOrder;
                if (order.orderState == OrderState.Ordered) {
                    log.info("Order store granted", order);
                    return setOrderStore(occupancy);
                }
                else {
                    log.info("Order pickup granted", order);
                    return setOrderPickup(occupancy);
                }
            }

            // check for order storage code
            const shopDelivery = findShopDelivery(shops, code);
            if (shopDelivery) {
                log.info("Shop delivery granted", shopDelivery);
                const {order, shop} = shopDelivery;
                setOrderDelivery(order);
                setShopAccess(shop.id);
                return;
            }

            // otherwise, code is invalid
            log.verbose("Invalid code");
            throw new Error("Invalid code.");
        }
    }, [shops, occupancies]);

    const handleBarcode = useCallback(async ({code}: Barcode) => {
        const barCode = atob(code);
        log.info("Received barcode", barCode);

        // barcode can be an access code
        await handleCodeVerify(barCode);

    }, [handleCodeVerify]);
    useBarcodeListener(handleBarcode);

    const currentShop = (shops && currentShopId && shops[currentShopId]) || undefined;

    const theme = getTheme(currentShop?.shopType);

    const content = useMemo(() => {
        if (currentShop) {
            const ShopDetail = getShopDetail(currentShop);
            return (
                <ShopDetail
                    shop={currentShop}
                    onBack={hasMultipleShops(shops) ? clearCurrentShop : undefined}
                />
            );
        }
        else if (shops) {
            return (
                <ShopList shops={shops} onSelect={handleSelectShop}/>
            );
        }
        else {
            // loading shops...
            return null;
        }
    }, [currentShop, shops, clearCurrentShop, handleSelectShop]);

    if (orderPickup && shops) {
        // show order pickup flow
        return <PickupFlow occupancy={orderPickup} shops={shops} onDone={handlePickupDone}/>;
    }
    else if (orderStore) {
        return <StoreFlow occupancy={orderStore} onDone={handleStoreDone}/>;
    }
    else if (shops && shopAccess) {
        // show shop menu
        const shop = shops[shopAccess];
        return (
            <ShopAccessView
                shop={shop}
                onDone={handleShopDone}
                allowOpenNonOccupied={Object.keys(shops).length == 1}
                orderDelivery={orderDelivery}
            />
        );
    }
    else {
        return (
            <div className={classNames(layout.horizontal, theme, styles.main)}>
                {content}
                <div className={classNames(layout.vertical, layout.spaceBetween)}>

                    <div
                        className={classNames(styles.locationCodeContainer, layout.paddingMedium, typography.alignCenter)}>
                        <div className={styles.locationCodeTitle}>{t("LocationCode")}</div>
                        <div className={styles.locationCode}>{locationCode}</div>
                    </div>

                    <div className={styles.buttons}>
                        <div className={styles.pickupButton} onClick={handleOpenCodeClick}>
                            <div className={classNames(styles.pickupIcon)}/>
                            <div className={styles.pickupText}>{t("Pickup")}</div>
                        </div>
                        <div className={styles.pickupButton} onClick={handleOpenCodeClick}>
                            <div className={styles.pickupText}>{t("Store")}</div>
                        </div>
                    </div>

                </div>
                {shops && (
                    <CodeDialog
                        open={codeOpen}
                        onClose={handleCloseCodeClick}
                        onVerify={handleCodeVerify}
                    />
                )}
            </div>
        );
    }
}

function getShopDetail(shop: Shop) {
    return shop.shopType == ShopType.DryCleaning ? DryCleaningHomeView : GenericShopHomeView;
}


const styles = stylesheet({
    main: {
        height: "100%",
    },
    locationCodeContainer: {
        margin: "1.5em 0",
        background: "#fff",
        boxShadow,
    },
    locationCodeTitle: {},
    locationCode: {
        fontWeight: "bold",
        fontSize: "2em"
    },
    buttons: {
        margin: "1.5em 0",
    },
    pickupButton: {
        background: "var(--color-primary)",
        boxShadow,
        color: "#fff",
        padding: "1em 2em",
        marginTop: "0.75em",
        cursor: "pointer"
    },
    pickupIcon: {
        backgroundImage: `url(${pickupIcon})`,
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat",
        height: "5em",
        width: "7em",
        margin: "1em auto",
        cursor: "pointer"
    },
    pickupText: {
        fontWeight: 600,
        fontSize: "2em",
        textAlign: "center"
    },
});

function hasMultipleShops(shops?: Shops) {
    return shops ? Object.keys(shops).length > 1 : false;
}

function getSingleShop(shops: Shops) {
    const ids = Object.keys(shops);
    if (ids.length == 1) {
        return shops[ids[0]];
    }
}

function findShopDelivery(shops: Shops, storageCode: string) {
    for (const shop of Object.values(shops)) {
        const order = shop.orders.find(order => order.storageCode == storageCode && order.orderState === OrderState.ReadyForDelivery);
        if (order) {
            return {order, shop};
        }
    }
}

function findOccupancyOrder(occupancies: Occupancy[], shops: Shops, code: string) {
    const occupancy = occupancies.find(occupancy => occupancy.accessCode === code);
    if (occupancy) {
        const occupancyOrder = getOccupancyOrder(occupancy);
        if (occupancyOrder) {
            for (const shop of Object.values(shops)) {
                const order = shop.orders.find(order => order.id == occupancyOrder.id);
                if (order) {
                    return {occupancy, order};
                }
            }
        }
    }
}

